import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import Headline from "elements/Headline";
import Label from "elements/Label";
import InputField from "components/form/InputField";
import RadioButtonsField from "components/form/RadioButtonsField";
import TextAreaField from "components/form/TextAreaField";
import IndentedSection from "elements/IndentedSection";
import { FormDataContext } from "context/FormDataContext";
import { ApiDataContext } from "context/ApiDataContext";
import { validateStep } from "utils/validateStep";
import handleFields from "utils/handleFields";
import DayOffField from "components/form/DayOffField";
import DaysOffField from "components/form/DaysOffField";
import WorkScheduleField from "components/form/WorkScheduleField";
import PurposeSalaryField from "components/form/PurposeSalaryField";
import Modal from "components/Modal";
import useModal from "hooks/useModal";
import HelpBtn from "elements/HelpBtn";
import styled from "styled-components";

function Step6({ headline, step }) {
  const { employment, jobType, validatedSteps, setValidatedSteps } =
    useContext(FormDataContext);

  const { apiData, labels } = useContext(ApiDataContext);

  const { isVisible, toggleModal, activeModal } = useModal();

  const defaultWorkScheduleData = [
    { dayoff: false, notime: false, start: "", end: "", hoursTotal: 0 },
    { dayoff: false, notime: false, start: "", end: "", hoursTotal: 0 },
    { dayoff: false, notime: false, start: "", end: "", hoursTotal: 0 },
    { dayoff: false, notime: false, start: "", end: "", hoursTotal: 0 },
    { dayoff: false, notime: false, start: "", end: "", hoursTotal: 0 },
    { dayoff: false, notime: false, start: "", end: "", hoursTotal: 0 },
    { dayoff: true, notime: false, start: "", end: "", hoursTotal: 0 },
  ];

  // WorkSchedule is valid by default, therefore only 7 fields
  const fieldsWithRequiredValidation = 7;
  const fields = handleFields(fieldsWithRequiredValidation, step, employment);

  useEffect(() => {
    validateStep(
      fields,
      step,
      employment,
      validatedSteps,
      setValidatedSteps,
      jobType
    );
  }, [employment]);

  return (
    <>
      <Headline>{headline}</Headline>
      <PurposeSalaryField
        step={step}
        name="purpose_salary"
        label={labels.purpose_salary}
        options={apiData.purpose_salary}
        value={employment?.purpose_salary?.value ?? []}
      />
      <RadioButtonsField
        step={step}
        name="employee_handbook"
        label={labels.employee_handbook}
        options={apiData.employee_handbook}
        value={employment?.employee_handbook?.value ?? ""}
      />
      <Label legend margintop>
        Aktuel kontaktperson
      </Label>
      <InputField
        step={step}
        name="contactName"
        label={labels.contactName}
        value={employment?.contactName?.value ?? ""}
      />
      <InputField
        step={step}
        name="contactAddress"
        label={labels.contactAddress}
        value={employment?.contactAddress?.value ?? ""}
      />
      <InputField
        step={step}
        name="contactPhoneNumber"
        label={labels.contactPhoneNumber}
        value={employment?.contactPhoneNumber?.value ?? ""}
      />
      <InputField
        step={step}
        name="contactEmail"
        label={labels.contactEmail}
        value={employment?.contactEmail?.value ?? ""}
        validationRequired={false}
      />
      {employment?.weekly_hours?.value && (
        <Label margintop>
          {`Der er på side 2 oplyst en ugentlig arbejdstid på 
        ${employment?.weekly_hours?.value} timer`}
        </Label>
      )}
      <Flex>
        <Label margintop legend>
          Fridag(e)
        </Label>
        <HelpBtn onClick={() => toggleModal("workschedule")}>?</HelpBtn>
      </Flex>
      {jobType === "gartner" || jobType === "gartneriarbejder" ? (
        <DaysOffField
          step={step}
          name="workSchedule"
          label={labels.daysOff}
          options={apiData.days}
          value={
            employment?.workSchedule?.value.length > 0
              ? employment?.workSchedule?.value
              : defaultWorkScheduleData
          }
        />
      ) : (
        <DayOffField
          step={step}
          name="workSchedule"
          label={labels.dayOff}
          options={apiData.days}
          value={
            employment?.workSchedule?.value.length > 0
              ? employment?.workSchedule?.value
              : defaultWorkScheduleData
          }
        />
      )}
      {jobType === "individuel" && (
        <RadioButtonsField
          step={step}
          name="workschedule_type"
          label={labels.workschedule_type}
          options={apiData.workschedule_type}
          value={employment?.workschedule_type?.value ?? ""}
        />
      )}
      <Flex>
        {employment?.workschedule_type?.value ? (
          <Label margintop legend>
            {employment?.weekly_hours?.value >= 37
              ? labels.supportTexts.fullTime[
                  employment?.workschedule_type?.value
                ] ?? labels.supportTexts.fullTime.default
              : labels.supportTexts.parttime[
                  employment?.workschedule_type?.value
                ] ?? labels.supportTexts.parttime.default}
          </Label>
        ) : (
          <Label margintop legend>
            {employment?.weekly_hours?.value >= 37
              ? labels.supportTexts.fullTime[jobType] ??
                labels.supportTexts.fullTime.default
              : labels.supportTexts.parttime[jobType] ??
                labels.supportTexts.parttime.default}
          </Label>
        )}
        <HelpBtn onClick={() => toggleModal("workschedule")}>?</HelpBtn>
      </Flex>
      <WorkScheduleField
        step={step}
        name="workSchedule"
        label={
          labels.supportTexts.parttime[jobType] ??
          labels.supportTexts.parttime.default
        }
        options={apiData.days}
        value={
          employment?.workSchedule?.value.length > 0
            ? employment?.workSchedule?.value
            : defaultWorkScheduleData
        }
      />
      {employment?.workschedule_type?.value ? (
        <RadioButtonsField
          step={step}
          name="workScheduleHasAdditionalNotes"
          label={
            labels.workScheduleHasAdditionalNotes[
              employment?.workschedule_type?.value
            ]
          }
          options={apiData.workScheduleHasAdditionalNotes}
          value={employment?.workScheduleHasAdditionalNotes?.value ?? ""}
        />
      ) : (
        <RadioButtonsField
          step={step}
          name="workScheduleHasAdditionalNotes"
          label={labels.workScheduleHasAdditionalNotes[jobType]}
          options={apiData.workScheduleHasAdditionalNotes}
          value={employment?.workScheduleHasAdditionalNotes?.value ?? ""}
        />
      )}
      {employment?.workScheduleHasAdditionalNotes?.value ===
        "workScheduleHasAdditionalNotes1" && (
        <IndentedSection>
          <TextAreaField
            step={step}
            name="workScheduleAdditionalNotes"
            extendedRequired={{
              requiredField: "workScheduleHasAdditionalNotes",
              requiredValue: "workScheduleHasAdditionalNotes1",
            }}
            label={labels.workScheduleAdditionalNotes}
            value={employment?.workScheduleAdditionalNotes?.value ?? ""}
          />
        </IndentedSection>
      )}
      <RadioButtonsField
        step={step}
        name="deals"
        label={labels.deals}
        options={apiData.deals}
        value={employment?.deals?.value ?? ""}
      />
      {employment?.deals?.value === "deals1" && (
        <IndentedSection>
          <TextAreaField
            step={step}
            name="dealsNotes"
            extendedRequired={{
              requiredField: "deals",
              requiredValue: "deals1",
            }}
            label={labels.dealsNotes}
            value={employment?.dealsNotes?.value ?? ""}
          />
        </IndentedSection>
      )}
      <Modal
        isVisible={isVisible && activeModal === "workschedule"}
        hideModal={toggleModal}
      >
        <h3>Bemærk</h3>
        <p>
          Det her er ikke et planlægningsværktøj, det er alene en indikation af
          den faste fridag, hvad den er aftalt til, og rammetiden, der er
          gældende for ansættelsen.
        </p>
        <p>
          (Det fritager ikke arbejdsgiveren fra at lave en arbejdstidsplan fire
          uger i forvejen).
        </p>
      </Modal>
    </>
  );
}

const Flex = styled.div`
  display: flex;
  align-items: flex-end;
`;

Step6.propTypes = {
  step: PropTypes.number,
  headline: PropTypes.string,
};

export default Step6;
